import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Check, Smartphone } from "lucide-react";
import { Reveal, ExternalLinkIcon, SectionLabel } from "./primitives";
import { businessDetails, images, appFeatures } from "../data/content";
import { track } from "../lib/analytics";

const ext = { target: "_blank", rel: "noopener noreferrer" };

export const AppSection = () => {
  const reduce = useReducedMotion();
  return (
    <section
      id="mobile-app"
      aria-labelledby="app-heading"
      className="bg-brand-bg py-16 lg:py-24"
    >
      <div className="mx-auto grid max-w-content items-center gap-12 px-4 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <Reveal>
          <SectionLabel>Free mobile app</SectionLabel>
          <h2
            id="app-heading"
            className="mt-3 font-heading text-3xl font-bold tracking-tight text-brand-navy lg:text-4xl"
          >
            Manage your IVA from your pocket
          </h2>
          <span className="mt-3 inline-flex items-center rounded-full bg-brand-teal/10 px-4 py-1.5 text-sm font-semibold text-brand-teal">
            Our customer app is free to download
          </span>
          <p className="mt-4 max-w-xl text-lg leading-relaxed text-brand-body">
            As a Freeman Jones customer, you can download our free customer app to stay in
            control of your arrangement throughout your plan. Track your progress, keep up to
            date, message your team, upload documents and feel at ease — all in one secure place.
          </p>

          <ul className="mt-7 space-y-3">
            {appFeatures.map((f) => (
              <li key={f.title} className="flex gap-3">
                <span className="mt-0.5 grid h-6 w-6 flex-shrink-0 place-items-center rounded-full bg-brand-teal/10 text-brand-teal">
                  <Check className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="text-base leading-relaxed text-brand-body">
                  <span className="font-semibold text-brand-navy">{f.title}.</span> {f.body}
                </span>
              </li>
            ))}
          </ul>

          <a
            {...ext}
            href={businessDetails.customerAppUrl}
            onClick={() => track("customer_app_clicked", { location: "app-section" })}
            className="mt-8 inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-brand-teal px-7 text-center text-base font-semibold text-white transition-colors hover:bg-brand-tealHover"
            data-testid="app-section-open-app"
            aria-label="Get the Freeman Jones Customer App — opens external customer service in a new tab"
          >
            <Smartphone className="h-5 w-5" aria-hidden="true" /> Get the Customer App
            <ExternalLinkIcon />
          </a>
          <p className="mt-3 text-sm text-brand-body">
            The customer app is free to download for Freeman Jones IVA customers.
          </p>
        </Reveal>

        <Reveal delay={0.1} className="flex justify-center">
          <motion.div
            initial={reduce ? false : { opacity: 0, y: 24 }}
            whileInView={reduce ? {} : { opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="relative mx-auto w-full max-w-[440px] py-6"
          >
            <div className="absolute inset-10 -z-10 rounded-full bg-brand-teal/10 blur-2xl" aria-hidden="true" />
            <img
              src={images.appMockup2}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute left-0 top-8 w-[54%] -rotate-[11deg] rounded-[22px] border border-brand-border shadow-lg"
            />
            <img
              src={images.appMockup3}
              alt=""
              aria-hidden="true"
              loading="lazy"
              className="absolute right-0 top-8 w-[54%] rotate-[11deg] rounded-[22px] border border-brand-border shadow-lg"
            />
            <img
              src={images.appMockup}
              alt="The free Freeman Jones customer app showing a My Budget screen that helps keep your IVA on track."
              width="900"
              height="1600"
              loading="lazy"
              className="relative z-10 mx-auto w-[58%] overflow-hidden rounded-[26px] border border-brand-border shadow-2xl"
            />
          </motion.div>
        </Reveal>
      </div>
    </section>
  );
};
